/**
 * S3 Analytics React Hook
 *
 * Custom React hook for fetching and managing S3-based analytics data.
 * Provides caching, error handling, and real-time updates.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  useQuery,
  useLazyQuery,
  useSubscription,
  useMutation,
} from "@apollo/client";
import {
  GET_S3_DASHBOARD_METRICS,
  GET_S3_LINK_ANALYTICS,
  GET_S3_TOP_PERFORMING_LINKS,
  GET_S3_HOURLY_TRENDS,
  GET_REAL_TIME_METRICS,
  ANALYTICS_REAL_TIME_UPDATES,
  CLEAR_ANALYTICS_CACHE,
  DashboardMetricsVariables,
  LinkAnalyticsVariables,
  DashboardMetrics,
  LinkAnalytics,
} from "../services/graphql/s3-analytics-operations";

// Date range helper
export interface DateRange {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
}

// Hook options
interface UseS3AnalyticsOptions {
  tenantId: string;
  dateRange: DateRange;
  autoRefresh?: boolean;
  refreshInterval?: number; // milliseconds
  enableRealTime?: boolean;
  cacheTimeout?: number; // milliseconds
}

// Hook return type
interface UseS3AnalyticsReturn {
  // Dashboard data
  dashboardMetrics: DashboardMetrics | null;
  dashboardLoading: boolean;
  dashboardError: any;

  // Link analytics
  linkAnalytics: LinkAnalytics | null;
  linkAnalyticsLoading: boolean;
  linkAnalyticsError: any;

  // Top performing links
  topLinks: any[] | null;
  topLinksLoading: boolean;
  topLinksError: any;

  // Hourly trends
  hourlyTrends: any[] | null;
  hourlyTrendsLoading: boolean;
  hourlyTrendsError: any;

  // Real-time data
  realTimeMetrics: any | null;
  realTimeLoading: boolean;
  realTimeError: any;

  // Actions
  refreshDashboard: () => void;
  getLinkAnalytics: (linkId: string) => void;
  setDateRange: (range: DateRange) => void;
  clearCache: () => Promise<void>;

  // State
  isRefreshing: boolean;
  lastUpdated: Date | null;
  clearCacheLoading: boolean;
}

export function useS3Analytics(
  options: UseS3AnalyticsOptions
): UseS3AnalyticsReturn {
  const {
    tenantId,
    dateRange,
    autoRefresh = false,
    refreshInterval = 300000, // 5 minutes
    enableRealTime = true,
    cacheTimeout = 300000, // 5 minutes
  } = options;

  // State
  const [currentDateRange, setCurrentDateRange] =
    useState<DateRange>(dateRange);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);

  // Dashboard metrics query
  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery(GET_S3_DASHBOARD_METRICS, {
    variables: {
      tenantId,
      dateFrom: currentDateRange.from,
      dateTo: currentDateRange.to,
    } as DashboardMetricsVariables,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
    skip: !tenantId,
  });

  // Link analytics lazy query
  const [
    getLinkAnalyticsQuery,
    {
      data: linkAnalyticsData,
      loading: linkAnalyticsLoading,
      error: linkAnalyticsError,
    },
  ] = useLazyQuery(GET_S3_LINK_ANALYTICS, {
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // Top performing links query
  const {
    data: topLinksData,
    loading: topLinksLoading,
    error: topLinksError,
    refetch: refetchTopLinks,
  } = useQuery(GET_S3_TOP_PERFORMING_LINKS, {
    variables: {
      tenantId,
      dateFrom: currentDateRange.from,
      dateTo: currentDateRange.to,
      limit: 10,
    },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    skip: !tenantId,
  });

  // Hourly trends query
  const {
    data: hourlyTrendsData,
    loading: hourlyTrendsLoading,
    error: hourlyTrendsError,
    refetch: refetchHourlyTrends,
  } = useQuery(GET_S3_HOURLY_TRENDS, {
    variables: {
      tenantId,
      dateFrom: currentDateRange.from,
      dateTo: currentDateRange.to,
    },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    skip: !tenantId,
  });

  // Real-time metrics query (for today's data)
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const shouldFetchRealTime = useMemo(
    () => enableRealTime && currentDateRange.to >= today,
    [enableRealTime, currentDateRange.to, today]
  );

  const {
    data: realTimeData,
    loading: realTimeLoading,
    error: realTimeError,
    refetch: refetchRealTime,
  } = useQuery(GET_REAL_TIME_METRICS, {
    variables: {
      tenantId,
      date: today,
    },
    skip: !shouldFetchRealTime || !tenantId,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    pollInterval: shouldFetchRealTime ? 30000 : 0, // Poll every 30 seconds for real-time data
  });

  // Real-time subscription for live updates - DISABLED due to MQTT not being supported
  // TODO: Configure WebSocket link for AppSync subscriptions
  const { data: subscriptionData } = useSubscription(
    ANALYTICS_REAL_TIME_UPDATES,
    {
      variables: { tenantId },
      skip: true, // Always skip until WebSocket is configured
      onSubscriptionData: ({ subscriptionData }) => {
        if (subscriptionData.data) {
          console.log("Real-time analytics update:", subscriptionData.data);
          setLastUpdated(new Date());

          // Optionally refresh queries when real-time updates come in
          if (shouldFetchRealTime) {
            refetchRealTime();
          }
        }
      },
    }
  );

  // Cache clearing mutation
  const [clearCacheMutation, { loading: clearCacheLoading }] = useMutation(
    CLEAR_ANALYTICS_CACHE,
    {
      onCompleted: (data) => {
        console.log("Cache cleared successfully:", data);
        // Refresh all queries after cache clear
        refreshDashboard();
      },
      onError: (error) => {
        console.error("Error clearing cache:", error);
      },
    }
  );

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      console.log("Auto-refreshing analytics data");
      refreshDashboard();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  // Update date range when options change
  useEffect(() => {
    setCurrentDateRange(dateRange);
  }, [dateRange]);

  // Actions
  const refreshDashboard = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchDashboard(),
        refetchTopLinks(),
        refetchHourlyTrends(),
        shouldFetchRealTime ? refetchRealTime() : Promise.resolve(),
      ]);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error refreshing dashboard:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [
    refetchDashboard,
    refetchTopLinks,
    refetchHourlyTrends,
    refetchRealTime,
    shouldFetchRealTime,
  ]);

  const getLinkAnalytics = useCallback(
    (linkId: string) => {
      setSelectedLinkId(linkId);
      getLinkAnalyticsQuery({
        variables: {
          linkId,
          tenantId,
          dateFrom: currentDateRange.from,
          dateTo: currentDateRange.to,
        } as LinkAnalyticsVariables,
      });
    },
    [getLinkAnalyticsQuery, tenantId, currentDateRange]
  );

  const setDateRange = useCallback((range: DateRange) => {
    setCurrentDateRange(range);
    // Queries will automatically refetch due to variable changes
  }, []);

  const clearCache = useCallback(async () => {
    if (!tenantId) {
      console.warn("Cannot clear cache: no tenant ID");
      return;
    }

    try {
      console.log("Clearing analytics cache for tenant:", tenantId);
      await clearCacheMutation({
        variables: { tenantId },
      });
    } catch (error) {
      console.error("Failed to clear analytics cache:", error);
    }
  }, [clearCacheMutation, tenantId]);

  // Memoized processed data
  const dashboardMetrics = useMemo(() => {
    if (!dashboardData?.getS3DashboardMetrics) return null;

    const metrics = dashboardData.getS3DashboardMetrics;

    // Combine with real-time data if available
    if (realTimeData?.getRealTimeMetrics && shouldFetchRealTime) {
      const realTime = realTimeData.getRealTimeMetrics;
      return {
        ...metrics,
        totalClicks: metrics.totalClicks + realTime.totalClicks,
        uniqueClicks: metrics.uniqueClicks + realTime.uniqueClicks,
        deviceBreakdown: {
          MOBILE:
            metrics.deviceBreakdown.MOBILE + realTime.deviceBreakdown.MOBILE,
          DESKTOP:
            metrics.deviceBreakdown.DESKTOP + realTime.deviceBreakdown.DESKTOP,
          TABLET:
            metrics.deviceBreakdown.TABLET + realTime.deviceBreakdown.TABLET,
          OTHER: metrics.deviceBreakdown.OTHER + realTime.deviceBreakdown.OTHER,
        },
      };
    }

    return metrics;
  }, [dashboardData, realTimeData, shouldFetchRealTime]);

  const linkAnalytics = useMemo(() => {
    return linkAnalyticsData?.getS3LinkAnalytics || null;
  }, [linkAnalyticsData]);

  const topLinks = useMemo(() => {
    return topLinksData?.getS3TopPerformingLinks || null;
  }, [topLinksData]);

  const hourlyTrends = useMemo(() => {
    return hourlyTrendsData?.getS3HourlyTrends || null;
  }, [hourlyTrendsData]);

  const realTimeMetrics = useMemo(() => {
    return realTimeData?.getRealTimeMetrics || null;
  }, [realTimeData]);

  return {
    // Dashboard data
    dashboardMetrics,
    dashboardLoading,
    dashboardError,

    // Link analytics
    linkAnalytics,
    linkAnalyticsLoading,
    linkAnalyticsError,

    // Top performing links
    topLinks,
    topLinksLoading,
    topLinksError,

    // Hourly trends
    hourlyTrends,
    hourlyTrendsLoading,
    hourlyTrendsError,

    // Real-time data
    realTimeMetrics,
    realTimeLoading,
    realTimeError,

    // Actions
    refreshDashboard,
    getLinkAnalytics,
    setDateRange,
    clearCache,

    // State
    isRefreshing,
    lastUpdated,
    clearCacheLoading,
  };
}

// Helper hook for date range management
export function useDateRange(initialRange?: DateRange) {
  const [dateRange, setDateRange] = useState<DateRange>(
    initialRange || {
      from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0], // 30 days ago
      to: new Date().toISOString().split("T")[0], // today
    }
  );

  const setLast7Days = useCallback(() => {
    const to = new Date().toISOString().split("T")[0];
    const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    setDateRange({ from, to });
  }, []);

  const setLast30Days = useCallback(() => {
    const to = new Date().toISOString().split("T")[0];
    const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    setDateRange({ from, to });
  }, []);

  const setLast90Days = useCallback(() => {
    const to = new Date().toISOString().split("T")[0];
    const from = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    setDateRange({ from, to });
  }, []);

  const setCustomRange = useCallback((from: string, to: string) => {
    setDateRange({ from, to });
  }, []);

  return {
    dateRange,
    setDateRange,
    setLast7Days,
    setLast30Days,
    setLast90Days,
    setCustomRange,
  };
}
